import type { FreeFormObject } from '@ctcells/contract'

import { readDimensions, readWells } from './labwareRead.ts'

import {
  PLATE_CHAMFER,
  PLATE_EDGE,
  PLATE_INSET,
  SCREW_AT,
  SCREW_R,
  type PocketFit,
} from './rackPlateGeometry.ts'

/**
 * **Opentrons 랩웨어 정의(schemaVersion 2) → 판 위 구멍 배치.** 순수 함수다 — SVG 없이 수만 낸다.
 *
 * eric 지시(2026-09-27): 「이 랩웨어가 오픈트론의 랩웨어야? 거기에 정보가 있으면 거기서 가지고 와서 그려.」
 * ⇒ 염색기 랩웨어 5건(팁 랙 2 · 튜브 랙 2 · 냉장 24구)은 로컬 레시피에 **Opentrons 표준 정의를 통째로**
 *    싣고 있다(`namespace: 'opentrons'`). 판 치수(`dimensions`) · 웰 중심(`x`·`y`) · 모양(`shape`) ·
 *    크기(`diameter` / `xDimension`·`yDimension`)를 **그대로** 옮긴다. 비율을 사진에서 재지 않는다.
 *
 * 🔴 **좌표계** — 정의는 mm · **원점 왼쪽 아래**(y 위로)다. 그림은 y 아래로라 `cy = yDimension − y` 로 뒤집는다.
 *    ⇒ 돌리지 않으면 `A1` 이 **왼쪽 위**다(Opentrons 표준 방향 · 긴 변 x 가 가로).
 * 🔴 **`rotate: 90`** = 그림을 통째로 **시계 방향 90°**(데크 판 · eric 2026-09-26 「전체를 회전」).
 *    `cx' = 판 세로 − cy = y` · `cy' = cx = x` · 판 폭 · 높이와 사각 구멍의 폭 · 높이가 맞바뀐다.
 *    ⇒ `A1` 은 **오른쪽 위**, 1열이 맨 위 줄이다. 10구 랙은 15 mL(1 · 2열)이 위 · 50 mL(3 · 4열)가 아래다.
 * 🔴 **`fitLong`** — 비율은 그대로 두고 **길이 단위만** 바꾼다(긴 변이 이 값이 되게 전부 곱한다).
 *    데크 판은 판 틀(`RackPlate` 의 살 · 나사 자리)이 긴 변 126 단위로 재여 있어 그 값에 맞춘다.
 * 🔴 **부분으로 세우지 않는다.** 치수가 없거나, 웰 하나라도 좌표 · 크기가 빠졌거나, 선언된 웰 수와 읽은
 *    수가 다르면 `null` — 0 이나 이웃 값으로 메우지 않는다(구멍 하나가 빈 그림은 「그 자리에 웰이 없다」로 읽힌다).
 * ⚠ 이름에 `opentrons` 가 붙었지만 **꼴이 같으면** 우리 전용 `full` 정의(하이픽챔버)도 이 함수로 선다
 *   (`labwareLayout` 의 `wells` 갈래). 꼴이 같은 것을 두 벌로 계산하지 않는다.
 */

export interface LayoutHole {
  /** 웰 이름. 정의에서 이름을 짝지을 수 없으면 `null` — 지어내지 않는다 */
  name: string | null
  /** 중심(그림 좌표 · y 아래로) */
  cx: number
  cy: number
  shape: 'circle' | 'rect'
  /** 원 반지름 */
  r: number
  /** 사각 폭 · 높이(원이면 지름) */
  w: number
  h: number
}

export interface DefinitionLayout {
  /** 판 폭 · 높이(그림 단위) */
  width: number
  height: number
  /** `ordering`(열 우선) 차례 */
  holes: LayoutHole[]
}

export interface OpentronsLayoutOptions {
  rotate?: 0 | 90
  fitLong?: number
}

/** 정의가 Opentrons 표준 정의인가 — `namespace` 와 스키마 판으로만 본다(추측하지 않는다) */
export function isOpentronsDefinition(def: FreeFormObject | null | undefined): boolean {
  return def?.['namespace'] === 'opentrons' && def['schemaVersion'] === 2
}

/** `parameters.loadName` — 없으면 `null` */
export function loadNameOf(def: FreeFormObject | null | undefined): string | null {
  const parameters = def?.['parameters']
  if (typeof parameters !== 'object' || parameters === null || Array.isArray(parameters))
    return null
  const loadName = (parameters as FreeFormObject)['loadName']
  return typeof loadName === 'string' ? loadName : null
}

/** `parameters.isTiprack` — 정의가 스스로 팁 랙이라 말할 때만 `true` */
export function isTiprackDefinition(def: FreeFormObject | null | undefined): boolean {
  const parameters = def?.['parameters']
  return (
    typeof parameters === 'object' &&
    parameters !== null &&
    (parameters as FreeFormObject)['isTiprack'] === true
  )
}

export function opentronsLayout(
  def: FreeFormObject,
  { rotate = 0, fitLong }: OpentronsLayoutOptions = {},
): DefinitionLayout | null {
  const dimensions = readDimensions(def)
  if (dimensions === null) return null
  const raw = def['wells']
  const declared =
    typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? Object.keys(raw).length : 0
  const wells = readWells(def)
  if (wells.length === 0 || wells.length !== declared) return null

  const k = fitLong === undefined ? 1 : fitLong / Math.max(dimensions.x, dimensions.y)
  const holes: LayoutHole[] = []
  for (const well of wells) {
    /* 표준 방향(돌리기 전) · mm */
    const cx = well.x
    const cy = dimensions.y - well.y
    let shape: LayoutHole['shape']
    let w: number
    let h: number
    if (well.shape === 'circular' && well.diameter !== undefined) {
      shape = 'circle'
      w = well.diameter
      h = well.diameter
    } else if (
      well.shape !== 'circular' &&
      well.xDimension !== undefined &&
      well.yDimension !== undefined
    ) {
      shape = 'rect'
      w = well.xDimension
      h = well.yDimension
    } else {
      return null
    }
    const turned = rotate === 90
    holes.push({
      name: well.name,
      cx: (turned ? dimensions.y - cy : cx) * k,
      cy: (turned ? cx : cy) * k,
      shape,
      r: shape === 'circle' ? (w / 2) * k : 0,
      w: (turned ? h : w) * k,
      h: (turned ? w : h) * k,
    })
  }

  return rotate === 90
    ? { width: dimensions.y * k, height: dimensions.x * k, holes }
    : { width: dimensions.x * k, height: dimensions.y * k, holes }
}

/** 포켓 선과 구멍 테 사이에 남길 틈(그림 단위) */
const POCKET_CLEAR = 0.8

/**
 * **구멍을 가로지르지 않는 포켓** — 정의의 구멍이 사진에서 잰 포켓(`PLATE_INSET` · `PLATE_CHAMFER`)보다
 * 판 끝에 가까우면 포켓을 그만큼 물린다. 넉넉하면 사진 값 그대로다(24구 · 6구 랙은 바뀌지 않는다).
 *
 * 🔴 **구멍을 옮기지 않는다** — 정의가 이긴다. 물러나는 것은 우리가 지은 포켓 선뿐이다
 *    (10구 랙 15 mL 1열은 정의상 판 끝에서 6.43 mm · 팁 랙 귀 구멍은 모따기 선에 걸린다).
 * ⚠ 하한: 포켓은 바깥 판(`PLATE_EDGE`) 안, 모따기는 귀 나사 구멍(`SCREW_AT` · `SCREW_R`)을 덮지 않는 데까지.
 */
export function pocketClearing(layout: DefinitionLayout): PocketFit {
  const { width, height, holes } = layout
  let inset = PLATE_INSET
  for (const h of holes) {
    const hw = h.w / 2
    const hh = h.h / 2
    inset = Math.min(
      inset,
      h.cx - hw - POCKET_CLEAR,
      width - h.cx - hw - POCKET_CLEAR,
      h.cy - hh - POCKET_CLEAR,
      height - h.cy - hh - POCKET_CLEAR,
    )
  }
  inset = Math.max(inset, PLATE_EDGE + 0.5)
  /* 모따기 선: 귀에서 dx + dy = 2·inset + chamfer. 구멍의 귀 쪽 끝이 그 선 밖(판 쪽)에 있어야 한다 */
  let line = 2 * inset + PLATE_CHAMFER
  for (const h of holes) {
    const dx = Math.min(h.cx, width - h.cx)
    const dy = Math.min(h.cy, height - h.cy)
    const reach = h.shape === 'circle' ? h.r * Math.SQRT2 : h.w / 2 + h.h / 2
    line = Math.min(line, dx + dy - reach - POCKET_CLEAR)
  }
  /* 나사 구멍 바깥 끝(귀 쪽 반대편)보다는 선이 멀어야 한다 */
  const screwFloor = 2 * SCREW_AT + SCREW_R * Math.SQRT2 + POCKET_CLEAR
  line = Math.max(line, screwFloor, 2 * inset)
  return { inset, chamfer: line - 2 * inset }
}
